import { Injectable, Injector } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GasprecioService } from 'src/app/modules/gasprecio/services/gasprecio.service';
import { SharedService } from 'src/app/modules/gasprecio/services/shared.service';

/**
 * Garantiza que el id del grupo esté en `localStorage` ANTES de activar el portal.
 *
 * Las secciones del tablero (Reporte Cruzado, Inventario, Mapa de Calor, Bitácora
 * de eventos…) y las de muchos otros componentes se calculan una sola vez, de
 * forma síncrona, en el constructor, a partir de `localStorage.group`. El login no
 * guarda ese id: hoy lo escribe el header cuando resuelve la estación, varios
 * requests después. Entrar al portal recién iniciada la sesión dejaba esos gates
 * en false para siempre —nunca se recalculan—, y solo un refresh los arreglaba
 * porque para entonces el id ya había quedado guardado.
 *
 * Como `logout()` hace `localStorage.clear()`, se reproducía en cada login limpio.
 *
 * Si el grupo no se puede resolver (error de red, estación sin grupo asignado) se
 * deja pasar igual: la app arranca con el comportamiento anterior en vez de
 * quedarse bloqueada en una pantalla vacía.
 */
@Injectable({ providedIn: 'root' })
export class GroupGuard implements CanActivate {

  /**
   * Los servicios se piden al Injector solo cuando hay que resolver el grupo, no
   * en el constructor: el router instancia TODOS los guards de `/gasprecio` aunque
   * AuthGuard rechace la ruta, y sin sesión eso construiría GasprecioService con
   * `idStation`/`rol` en null. Como el login ya no recarga la página, ese singleton
   * se quedaría así para toda la sesión (portal sin datos hasta refrescar).
   */
  constructor(private injector: Injector) { }

  canActivate(): Observable<boolean> | boolean {
    // Ya resuelto (navegación normal o sesión previa): no cuesta nada.
    if (localStorage.getItem('group')) {
      return true;
    }

    // Sin estación no hay grupo que pedir; de la sesión se encarga AuthGuard.
    if (!localStorage.getItem('idStation')) {
      return true;
    }

    return this.injector.get(GasprecioService).getGroup().pipe(
      map((group: any) => {
        if (group?._id) {
          localStorage.setItem('group', group._id);
          this.injector.get(SharedService).nextGroup = group._id;
        }
        return true;
      }),
      catchError(() => of(true))
    );
  }

}
